import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { cx } from '../../lib/format';
import { useMediaQuery } from './Layout';
import { Button } from './Button';
import { Field, TextArea } from './Field';

function useOverlayBehavior(open: boolean, onClose: () => void, panel: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => {
      const target = panel.current?.querySelector<HTMLElement>('[data-autofocus], input:not([type=hidden]):not([disabled]), select, textarea, button:not([data-close])');
      (target ?? panel.current)?.focus();
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Tab' && panel.current) {
        // Mantém o foco dentro do painel.
        const items = [...panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open, onClose, panel]);
}

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'md' | 'lg' | 'xl';
  headerExtra?: ReactNode;
}

const panelSpring = { type: 'spring', stiffness: 420, damping: 38, mass: 0.9 } as const;

/**
 * Painel lateral que preserva o contexto da lista. No celular ocupa a tela inteira (formulários longos),
 * com cabeçalho e ações sempre acessíveis e rolagem interna previsível.
 */
export function Sheet({ open, onClose, title, subtitle, children, footer, width = 'md', headerExtra }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);
  useOverlayBehavior(open, onClose, panel);
  const mobile = useMediaQuery('(max-width: 639px)');
  const w = { md: 'sm:w-[480px]', lg: 'sm:w-[620px]', xl: 'sm:w-[760px]' }[width];
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <m.div className="absolute inset-0 bg-overlay" onClick={onClose} aria-hidden initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} />
          <m.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : undefined}
            tabIndex={-1}
            initial={mobile ? { y: '100%' } : { x: 32, opacity: 0 }}
            animate={mobile ? { y: 0 } : { x: 0, opacity: 1 }}
            exit={mobile ? { y: '100%' } : { x: 32, opacity: 0 }}
            transition={panelSpring}
            className={cx(
              'absolute flex flex-col bg-bg shadow-lg outline-none',
              'inset-x-0 bottom-0 top-[max(8px,var(--safe-top))] rounded-t-[22px]',
              'sm:inset-y-3 sm:left-auto sm:right-3 sm:top-3 sm:rounded-[22px]',
              w,
            )}
          >
            <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line-strong sm:hidden" aria-hidden />
            <header className="flex shrink-0 items-start gap-3 border-b border-separator px-5 pb-4 pt-3 sm:pt-5">
              <div className="min-w-0 flex-1">
                <h2 className="title-2 break-words">{title}</h2>
                {subtitle && <div className="mt-1 text-[13px] text-ink-2">{subtitle}</div>}
              </div>
              {headerExtra}
              <Button data-close variant="ghost" size="icon" onClick={onClose} aria-label="Fechar" className="-mr-2 -mt-1 rounded-full">
                <X />
              </Button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">{children}</div>
            {footer && <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-separator bg-bg px-5 py-3.5 pb-[max(14px,var(--safe-bottom))] [&>button]:max-sm:flex-1">{footer}</footer>}
          </m.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}

/** Diálogo proporcional; no celular vira folha inferior (ação curta ao alcance do polegar). */
export function Dialog({ open, onClose, title, children, footer }: DialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  useOverlayBehavior(open, onClose, panel);
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center p-3 pb-[max(12px,var(--safe-bottom))] sm:items-center">
          <m.div className="absolute inset-0 bg-overlay" onClick={onClose} aria-hidden initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} />
          <m.div
            ref={panel}
            role="alertdialog"
            aria-modal="true"
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 6 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full max-w-[440px] rounded-[22px] bg-elevated p-5 shadow-lg outline-none"
          >
            <h2 className="headline text-[17px]">{title}</h2>
            {children && <div className="mt-2 text-[14px] text-ink-2">{children}</div>}
            {footer && <div className="mt-5 flex flex-wrap justify-end gap-2 [&>button]:max-sm:flex-1">{footer}</div>}
          </m.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/** Confirmação de ação relevante, com motivo opcional/obrigatório. */
export function ConfirmDialog({
  open,
  onClose,
  title,
  message,
  confirmLabel,
  tone = 'primary',
  reason,
  onConfirm,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  reason?: { label: string; required: boolean; placeholder?: string };
  onConfirm: (reason: string) => void;
  loading?: boolean;
  error?: string | null;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) setText('');
  }, [open]);
  const invalid = !!reason?.required && text.trim().length < 3;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={loading} disabled={invalid} onClick={() => onConfirm(text.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message}
      {reason && (
        <div className="mt-4">
          <Field label={reason.label} required={reason.required} error={error}>
            {(p) => <TextArea {...p} data-autofocus value={text} onChange={(e) => setText(e.target.value)} placeholder={reason.placeholder} />}
          </Field>
        </div>
      )}
      {!reason && error && <p className="mt-3 text-[13px] font-medium text-danger">{error}</p>}
    </Dialog>
  );
}
